import React, { PropsWithChildren, useState } from "react";
import classNames from "classnames/bind";

import { IconDotLoader } from "../IconDotLoader";

import styles from "./Button.module.css";

const cx = classNames.bind(styles);

type Props = {
  block?: boolean;
  color?: "white" | "cerulean" | "dark";
  title?: string;
  component?: string;
  disabled?: boolean;
  href?: string;
  isLoading?: boolean;
  size?: "small" | "big" | "medium";
  type?: "button" | "submit";
  rounded?: boolean;
  onClick?: () => void | Promise<void>;
};

const getComponent = (props: Props) => {
  const { href } = props;

  if (href) {
    return "a" as unknown as React.ComponentType<any>;
  }

  return "button" as unknown as React.ComponentType<any>;
};

export const Button = React.forwardRef<
  HTMLButtonElement,
  PropsWithChildren<Props>
>(
  (
    {
      block,
      children,
      color,
      disabled,
      href,
      isLoading,
      onClick,
      size,
      type,
      title,
      rounded,
    },
    ref
  ) => {
    const ComponentName = getComponent({ href });
    const [isLoadingState, setIsLoadingState] = useState<boolean>(false);

    const handleClick = async () => {
      if (!onClick) {
        return;
      }

      setIsLoadingState(true);
      await onClick();
      setIsLoadingState(false);
    };

    return (
      <ComponentName
        onClick={handleClick}
        className={cx("button", {
          button_loading: isLoading || isLoadingState,
          button_disabled: disabled || isLoading || isLoadingState,
          button_block: block,
          button_rounded: rounded,
          [`button_size_${size}`]: size,
          [`button_color_${color}`]: color,
        })}
        disabled={disabled || isLoading || isLoadingState}
        href={href}
        title={title}
        type={type}
        ref={ref}
      >
        <span className={cx("box")}>{children}</span>

        <span className={cx("loader")}>
          <IconDotLoader />
        </span>
      </ComponentName>
    );
  }
);
